import { AllSameKeyValuesEnforcer, compactDate, flatten, makeMapFromListOfObjectsAndKey, str2Date, time_to_min } from 'globals/helpers/helpers';
import { createSelector } from '@ngrx/store';
import { Program, Session } from 'globals/store/programs.data';
import { Dictionary } from '@ngrx/entity';
import { WorkerWithProfile } from 'globals/store/workforce.data';
import { Performer, performersStateSelector } from 'src/app/store/performers.era';
import { Sponsor } from 'globals/store/sponsors.data';
import { Exhibitor } from 'globals/store/exhibitors.data';
import { TagPanelData, getTagPanelData } from 'globals/store/tags.data';
import { programsArray } from './programs.reselector';
import { JoiResource } from 'globals/store/resources.data';
import { getSessionList } from '../../share/onsite.reselector.functions';
import { NestedProgramInfo, SessionInfo, fullSessionsEntitiesReselector } from './performerSponsors.reselector';
import { DAY_MS } from 'globals/helpers/constants';
import { permissionsStateSelector } from '../permissions.era';
import { accountStateSelector } from '../accounts.era';
import { eventStateSelector } from '../events.era';
import { programsStateSelector } from '../programs.era';
import { workforceStateSelector } from '../workforce.era';
import { sponsorsStateSelector } from '../sponsors.era';
import { exhibitorsStateSelector } from '../exhibitors.era';
import { resourcesStateSelector } from '../resources.era';
import { attendeesStateSelector } from '../attendees.era';

const accessPermissions = createSelector(permissionsStateSelector, (store) => store.access.types)
const account = createSelector(accountStateSelector, (store) => store.account)
const event = createSelector(eventStateSelector, (store) => store.event)
const programs = createSelector(programsStateSelector, (store) => store.entities)
const performers = createSelector(performersStateSelector, (store) => store.entities)
const workersByEmail = createSelector(workforceStateSelector, (store) => store.workersByEmail)
const sponsors = createSelector(sponsorsStateSelector, (store) => store.entities)
const exhibitors = createSelector(exhibitorsStateSelector, (store) => store.entities)
const resources = createSelector(resourcesStateSelector, (store) => store.entities)
const attendees = createSelector(attendeesStateSelector, (store) => store.entities)

export interface ManageSessionStates { ReadOnly, Program, Logistics }
export const manageSessionStates: AllSameKeyValuesEnforcer<ManageSessionStates> = { ReadOnly: 'ReadOnly', Program: 'Program', Logistics: 'Logistics'}


export const sessionListReselector = createSelector(
  programs,
  getSessionList
) 

export const sessionMapReselector = createSelector(
  sessionListReselector,
  (sessionList) => makeMapFromListOfObjectsAndKey(sessionList, 'id')
)


export const sessionReselector = createSelector(
  sessionMapReselector,
  (sessions: Dictionary<Session>, sessionId: string) => (sessions || {})[sessionId]
)

export const assetSessionListReselector = createSelector(
  programsArray,
  (programList) => flatten(programList.map(p => Object.values(p.sessions_obj).map(s => {
    const date = !p.date ? `Day ${s.on_day} ${s.start}-${s.end}` : 
      `${compactDate(str2Date(p.date))} ${s.start}-${s.end}`
    // s.assetSessionTitle = `${p.title || 'Unnamed Program'} - ${s.title || 'Unnamed Session'} - ${date}`
    return {...s, assetSessionTitle: `${p.title || 'Unnamed Program'} - ${s.title || 'Unnamed Session'} - ${date}`}
  })))
)

// includes out of session (using in cue sheets for now)
export const sessionProgramListReselector = createSelector(
  programsArray,
  (programs) =>{
    const sessionProgramList: NestedProgramInfo[] = []
    programs.forEach(({ id: programId, sessions, title: programTitle, sessions_obj, date: firstDay }) => {
      const sessionList: any[] = []
      sessionProgramList.push({ programId, programTitle, sessionList })
      sessions.forEach(d => {
        const day = !firstDay ? `Day ${d.day}` : null
        d.sessions.map(sId => sessions_obj[sId]).forEach(({ id, title, start, end }) => {
          const date = firstDay ? new Date(str2Date(firstDay).getTime() + (+d.day - 1) * DAY_MS + time_to_min(start) * 60000) : new Date((+d.day*2400 + +start.split(":").join(""))*60000) // this last part is for sorting only 
          sessionList.push({ id, title, start, end, selected: false, day, date })
        })
      })
    })
    // console.log({ sessionProgramList })
    return sessionProgramList
  }
  
)

export const sessionTagReselector = createSelector(
  programs,
  workersByEmail,
  performers,
  sponsors,
  exhibitors,
  resources,
  attendees,
  (programs: Dictionary<Program>, workersByEmail: { [email:string]: WorkerWithProfile }, performers: { [id:string]: Performer }, sponsors: { [id:string]: Sponsor }, exhibitors: { [id:string]: Exhibitor }, resources: Dictionary<JoiResource>, attendees, { sessionId, programId }: { sessionId: string, programId: string }): TagPanelData => {

    const program = programs[programId]
    if (!program) return null
    const session = program.sessions_obj[sessionId]
    if (!session) return null

    const { tags, performerTags, exhibitorTags, sponsorTags, resourceTags, attendeeTags } = session
    return getTagPanelData(tags, performerTags, sponsorTags, exhibitorTags, workersByEmail, performers, sponsors, exhibitors, resourceTags, resources, attendeeTags, attendees)
  }
)

export const manageSessionPermissionReselector = createSelector(
  accessPermissions,
  event,
  account,
  (accessPermissions, event, account) => {
    console.log({ accessPermissions, event, account, structuredData: true })
    if (!accessPermissions || !event || event.closed || !account || !account.docket || account.docket.status === 'suspended') return manageSessionStates.ReadOnly
    if (accessPermissions.includes('program')) return manageSessionStates.Program
    if (accessPermissions.includes('logistics')) return manageSessionStates.Logistics
    return manageSessionStates.ReadOnly
  }
)

export const manageSessionReadonlyReselector = createSelector(
  manageSessionPermissionReselector,
  event,
  (state, event) => !state || state === 'ReadOnly' || !event || event.is_readonly
)

